<template>
  <AppLayout
    page-title="Daily Report"
    page-subtitle="Supervisor operations report — tanks, well head, tank sheet"
  >
    <div class="daily-report-page pt-2">
      <header class="toolbar no-print">
        <div class="flex items-center gap-3 flex-wrap">
          <router-link to="/employee" class="back">← Dashboard</router-link>
          <select v-model="locationId" class="loc-select" @change="loadReport">
            <option disabled :value="0">Select location…</option>
            <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
          </select>
          <select v-model="report.report_time" class="loc-select" @change="loadReport">
            <option v-for="t in schedule" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
        <div class="actions">
          <button class="btn secondary" type="button" @click="loadReport">Refresh Data</button>
          <button class="btn secondary" type="button" @click="printReport">Print / PDF</button>
          <button class="btn secondary" type="button" :disabled="saving || !locationId" @click="saveReport">
            {{ saving ? 'Saving…' : 'Save Draft' }}
          </button>
          <button class="btn primary" type="button" :disabled="sending || !locationId" @click="sendReport">
            {{ sending ? 'Sending…' : 'Send Report' }}
          </button>
        </div>
      </header>

      <div v-if="!isSupervisor" class="notice error no-print">
        Daily Reports are available to Supervisor 1–3 only. Your crew level is {{ crewLabel }}.
      </div>

      <template v-else>
        <div v-if="saved" class="notice success no-print">Daily report saved.</div>
        <div v-if="sent" class="notice success no-print">Daily report sent successfully.</div>
        <div v-if="errorMessage" class="notice error no-print">{{ errorMessage }}</div>
        <div v-if="loading" class="notice no-print">Loading report data…</div>

        <section v-if="locationId" class="sheet">
          <div class="sheet-head">
            <div>
              <span class="eyebrow">OilBuddyAI</span>
              <h1>Daily Operations Report</h1>
              <p>Auto-synced from Tanks, Tank Sheet, and Well Head Sheet</p>
            </div>
            <span class="status" :class="report.status">{{ report.status }}</span>
          </div>

          <div class="meta-grid">
            <label><span>Report Date</span><input v-model="report.report_date" type="date" @change="loadReport"></label>
            <label><span>Report Time</span><input v-model="report.report_time" type="time" @change="loadReport"></label>
            <label>
              <span>Shift</span>
              <select v-model="report.shift"><option>Day</option><option>Night</option></select>
            </label>
            <label><span>Supervisor</span><input v-model="report.supervisor" placeholder="Supervisor name"></label>
            <label><span>Customer</span><input v-model="report.customer" placeholder="Customer"></label>
            <label><span>Operator</span><input v-model="report.operator" placeholder="Operator"></label>
            <label><span>Well Name</span><input v-model="report.well_name" placeholder="Well name"></label>
            <label><span>Company Man</span><input v-model="report.company_man" placeholder="Company man"></label>
          </div>

          <div class="recipient no-print">
            <label>
              <span>Send To</span>
              <input v-model="report.company_man_email" type="email" placeholder="companyman@operator.com">
            </label>
            <b>Live Sync Enabled</b>
          </div>

          <section class="block">
            <div class="section-title"><h2>Production Summary</h2><span>Current reporting hour</span></div>
            <div class="summary">
              <article><span>Total Fluid</span><strong>{{ totalFluid.toFixed(1) }}</strong><small>BBL</small></article>
              <article><span>Total Oil</span><strong>{{ totalOil.toFixed(1) }}</strong><small>BBL</small></article>
              <article><span>Total Water</span><strong>{{ totalWater.toFixed(1) }}</strong><small>BBL</small></article>
              <article><span>Water Meter Rate</span><strong>{{ report.tank_sheet?.water_meter_rate ?? '—' }}</strong></article>
              <article><span>Oil Meter Rate</span><strong>{{ report.tank_sheet?.oil_meter_rate ?? '—' }}</strong></article>
            </div>
          </section>

          <section class="block">
            <div class="section-title"><h2>Tank Battery</h2><span>Auto-filled from Tanks</span></div>
            <div class="scroller no-print">
              <div v-for="unit in commanderReportUnits" :key="`sc-${unit.id}`" class="mini commander">
                <SandCommanderVisual
                  :name="unit.name"
                  :bays="commanderVisualBays(unit)"
                  :capacity-bbl="110"
                  :width-px="420"
                />
              </div>
              <div v-for="tank in reportTanksWithoutCommander" :key="`vis-${tank.id}`" class="mini">
                <FracTankVisual
                  :name="tank.name"
                  :capacity-bbl="tankCuts(tank).capacityBbl"
                  :spill-inches="tankCuts(tank).spillInches"
                  :oil-inches="tankCuts(tank).oilInches"
                  :water-inches="tankCuts(tank).waterInches"
                  :fill-token="tank.measured_at || tank.id"
                  :width-px="tankCuts(tank).spillInches > 150 ? 180 : 160"
                  :tank-style="(tank.tank_style || tank.tankStyle) === 'upright' ? 'upright' : 'frac'"
                />
                <span v-if="tank.flag || tank.watch_status" class="mini-flag">{{ tank.flag || tank.watch_status }}</span>
              </div>
            </div>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tank</th><th>Oil Strap</th><th>Water Strap</th><th>Oil BBL</th>
                    <th>Water BBL</th><th>Total BBL</th><th>Gain / Loss</th><th>Last Reading</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tank in report.tanks" :key="tank.id">
                    <td><b>{{ tank.name }}</b></td>
                    <td>{{ tank.oil_inches ?? '—' }} in</td>
                    <td>{{ tank.water_inches ?? '—' }} in</td>
                    <td>{{ Number(tank.oil_bbl || 0).toFixed(1) }}</td>
                    <td>{{ Number(tank.water_bbl || 0).toFixed(1) }}</td>
                    <td>{{ Number(tank.total_bbl || 0).toFixed(1) }}</td>
                    <td>{{ Number(tank.gain_loss || 0).toFixed(1) }}</td>
                    <td>{{ tank.measured_at_human || '—' }}</td>
                  </tr>
                  <tr v-if="!report.tanks.length">
                    <td colspan="8" class="empty">No tank readings for this reporting hour.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="block">
            <div class="section-title"><h2>Well Head Data</h2><span>Auto-filled from Well Head Sheet</span></div>
            <div class="data-grid">
              <div><span>Flow °F</span><b>{{ report.well_head?.flow_temp ?? '—' }}</b></div>
              <div><span>Tubing PSI</span><b>{{ report.well_head?.tubing_psi ?? '—' }}</b></div>
              <div><span>Casing PSI</span><b>{{ report.well_head?.casing_psi ?? '—' }}</b></div>
              <div><span>Choke 64's</span><b>{{ report.well_head?.choke_64s ?? '—' }}</b></div>
              <div><span>Orifice</span><b>{{ report.well_head?.orifice ?? '—' }}</b></div>
              <div><span>Diff</span><b>{{ report.well_head?.diff ?? '—' }}</b></div>
              <div><span>Static PSI</span><b>{{ report.well_head?.static_psi ?? '—' }}</b></div>
              <div><span>Meter Temp</span><b>{{ report.well_head?.meter_temp ?? '—' }}</b></div>
              <div><span>Gas Rate</span><b>{{ report.well_head?.gas_rate ?? '—' }}</b></div>
              <div><span>Incremental Fluid</span><b>{{ report.well_head?.incremental_fluid ?? '—' }}</b></div>
              <div><span>Inc. Oil</span><b>{{ report.well_head?.incremental_oil ?? '—' }}</b></div>
              <div><span>Inc. Water</span><b>{{ report.well_head?.incremental_water ?? '—' }}</b></div>
              <div><span>Rate BPM</span><b>{{ report.well_head?.rate_bpm ?? '—' }}</b></div>
              <div><span>B.S.W.</span><b>{{ report.well_head?.bsw ?? '—' }}</b></div>
              <div><span>Salinity</span><b>{{ report.well_head?.salinity ?? '—' }}</b></div>
              <div><span>Sand</span><b>{{ report.well_head?.sand ?? '—' }}</b></div>
            </div>
          </section>

          <section class="block">
            <div class="section-title"><h2>Tank Sheet / Meter Data</h2><span>Auto-filled from Tank Sheet</span></div>
            <div class="data-grid meter">
              <div><span>Water Meter Total</span><b>{{ report.tank_sheet?.water_meter_total ?? '—' }}</b></div>
              <div><span>Water Meter Rate</span><b>{{ report.tank_sheet?.water_meter_rate ?? '—' }}</b></div>
              <div><span>Oil Meter Total</span><b>{{ report.tank_sheet?.oil_meter_total ?? '—' }}</b></div>
              <div><span>Oil Meter Rate</span><b>{{ report.tank_sheet?.oil_meter_rate ?? '—' }}</b></div>
              <div><span>Water Gain</span><b>{{ report.tank_sheet?.water_gain ?? '—' }}</b></div>
              <div><span>Oil Gain</span><b>{{ report.tank_sheet?.oil_gain ?? '—' }}</b></div>
              <div><span>Ticket #</span><b>{{ report.tank_sheet?.ticket_number ?? '—' }}</b></div>
              <div><span>Hauled</span><b>{{ report.tank_sheet?.hauled ?? '—' }}</b></div>
            </div>
          </section>

          <section class="block">
            <div class="section-title"><h2>Supervisor Notes</h2><span>Included in sent report</span></div>
            <textarea v-model="report.notes" rows="5" placeholder="Operational notes, trucking, sand, pressure changes, equipment notes, etc."></textarea>
          </section>

          <footer>
            <div><span>Prepared By</span><b>{{ report.supervisor || 'Supervisor' }}</b></div>
            <div><span>Report Status</span><b>{{ report.status }}</b></div>
            <div><span>Generated</span><b>OilBuddyAI</b></div>
          </footer>
        </section>

        <section v-if="recent.length" class="recent no-print">
          <h2>Recent Reports</h2>
          <div class="recent-list">
            <div
              v-for="r in recent"
              :key="r.id"
              class="recent-item"
            >
              <button type="button" class="recent-open" @click="openRecent(r)">
                <span>{{ r.reportDate }} {{ r.reportTime }}</span>
                <span>{{ r.wellName || 'Location ' + r.locationId }}</span>
                <span class="capitalize">{{ r.status }}</span>
              </button>
              <button
                v-if="r.status === 'draft'"
                type="button"
                class="recent-delete"
                :disabled="deletingId === r.id"
                @click="deleteReport(r)"
              >{{ deletingId === r.id ? 'Deleting…' : 'Delete' }}</button>
            </div>
          </div>
        </section>
      </template>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AppLayout from '@/components/layout/AppLayout.vue'
import FracTankVisual from '@/components/tanks/FracTankVisual.vue'
import SandCommanderVisual from '@/components/tanks/SandCommanderVisual.vue'
import { commanderPrimary, commanderVisualBays, groupCommanderUnits, tankCuts, tankKind } from '@/components/tanks/fracTank'
import { buildApiUrl } from '@/config/api'

const loading = ref(false)
const saving = ref(false)
const sending = ref(false)
const deletingId = ref<number | null>(null)
const saved = ref(false)
const sent = ref(false)
const errorMessage = ref('')
const isSupervisor = ref(true)
const crewLabel = ref('')
const locationId = ref(0)
const locations = ref<any[]>([])
const recent = ref<any[]>([])
const schedule = ref(['06:00', '10:00', '14:00', '18:00', '22:00', '02:00'])

const report = reactive<any>({
  id: null,
  report_date: new Date().toISOString().slice(0, 10),
  report_time: '06:00',
  shift: 'Day',
  customer: '',
  operator: '',
  well_name: '',
  company_man: '',
  company_man_email: '',
  supervisor: '',
  status: 'draft',
  tanks: [],
  well_head: {},
  tank_sheet: {},
  notes: '',
})

const commanderReportUnits = computed(() =>
  groupCommanderUnits((report.tanks || []).filter((tank: any) => tankKind(tank.tank_type || tank.tankType).style === 'commander')),
)
const reportTanksWithoutCommander = computed(() =>
  (report.tanks || []).filter((tank: any) => tankKind(tank.tank_type || tank.tankType).style !== 'commander'),
)
const totalOil = computed(() => report.tanks.reduce((s: number, t: any) => s + Number(t.oil_bbl || 0), 0))
const totalWater = computed(() => report.tanks.reduce((s: number, t: any) => s + Number(t.water_bbl || 0), 0))
const totalFluid = computed(() => report.tanks.reduce((s: number, t: any) => s + Number(t.total_bbl || 0), 0))

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'Content-Type': 'application/json' }
}

async function bootstrap() {
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl('employee/daily-reports'), { headers: authHeaders() })
    const data = await res.json()
    if (res.status === 403) {
      isSupervisor.value = false
      crewLabel.value = 'not supervisor'
      return
    }
    if (!res.ok) throw new Error(data.error || 'Failed to load')
    isSupervisor.value = true
    locations.value = data.locations || []
    recent.value = data.reports || []
    if (data.schedule?.length) schedule.value = data.schedule
    report.supervisor = data.employee?.name || report.supervisor
    if (!locationId.value && locations.value.length) {
      // Prefer Demo Battery (has tanks) then first
      const demo = locations.value.find((l: any) => /battery|demo/i.test(l.name)) || locations.value[0]
      locationId.value = demo.id
    }
    if (locationId.value) await loadReport()
  } catch (e: any) {
    errorMessage.value = e.message || 'Failed to load'
  }
}

async function loadReport() {
  if (!locationId.value) return
  loading.value = true
  errorMessage.value = ''
  try {
    const qs = new URLSearchParams({
      report_date: report.report_date,
      report_time: report.report_time,
    })
    const res = await fetch(
      buildApiUrl(`employee/locations/${locationId.value}/daily-reports/current?${qs}`),
      { headers: authHeaders() },
    )
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || data.message || 'Could not load daily report.')
    Object.assign(report, data.report || {})
    if (data.locations) locations.value = data.locations
  } catch (e: any) {
    errorMessage.value = e.message || 'Could not load daily report.'
  } finally {
    loading.value = false
  }
}

async function saveReport() {
  if (!locationId.value) return
  saving.value = true
  saved.value = false
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(`employee/locations/${locationId.value}/daily-reports`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(report),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || data.message || 'Could not save daily report.')
    Object.assign(report, data.report || {})
    saved.value = true
    setTimeout(() => (saved.value = false), 2200)
    await bootstrapRecentOnly()
  } catch (e: any) {
    errorMessage.value = e.message || 'Could not save daily report.'
  } finally {
    saving.value = false
  }
}

async function bootstrapRecentOnly() {
  try {
    const res = await fetch(buildApiUrl('employee/daily-reports'), { headers: authHeaders() })
    const data = await res.json()
    if (res.ok) recent.value = data.reports || []
  } catch { /* ignore */ }
}

async function deleteReport(item: any) {
  if (!window.confirm(`Delete draft report from ${item.reportDate} ${item.reportTime}? This cannot be undone.`)) return
  deletingId.value = Number(item.id)
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(`employee/daily-reports/${item.id}`), {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Delete failed')
    recent.value = recent.value.filter((entry) => entry.id !== item.id)
    if (report.id === item.id) {
      report.id = null
      report.status = 'draft'
    }
  } catch (e: any) {
    errorMessage.value = e.message || 'Delete failed'
  } finally {
    deletingId.value = null
  }
}

async function sendReport() {
  if (!report.company_man_email) {
    errorMessage.value = 'Enter the company man email before sending.'
    return
  }
  if (!report.id) await saveReport()
  if (!report.id) return
  sending.value = true
  sent.value = false
  errorMessage.value = ''
  try {
    const res = await fetch(buildApiUrl(`employee/daily-reports/${report.id}/send`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        recipient_name: report.company_man,
        recipient_email: report.company_man_email,
      }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || data.message || 'Could not send daily report.')
    report.status = data.report?.status || 'sent'
    sent.value = true
    setTimeout(() => (sent.value = false), 2400)
    await bootstrapRecentOnly()
  } catch (e: any) {
    errorMessage.value = e.message || 'Could not send daily report.'
  } finally {
    sending.value = false
  }
}

function openRecent(r: any) {
  locationId.value = r.locationId
  report.report_date = r.reportDate
  report.report_time = r.reportTime
  loadReport()
}

const printReport = () => window.print()

onMounted(bootstrap)
</script>

<style scoped>
.daily-report-page{color:#dce9f5}
.toolbar{display:flex;justify-content:space-between;gap:12px;align-items:center;margin-bottom:14px;flex-wrap:wrap}
.back{color:#8eb4d4;text-decoration:none;font-size:13px}
.loc-select{background:#23344a;border:1px solid #344a62;color:#eaf3fa;border-radius:8px;padding:8px 10px}
.actions{display:flex;gap:8px;flex-wrap:wrap}
.btn{border:1px solid #344a62;border-radius:8px;padding:9px 14px;font-weight:800;cursor:pointer}
.secondary{background:#23344a;color:#eaf3fa}
.primary{background:#087a66;border-color:#0e957c;color:#fff}
.notice{margin:0 auto 10px;padding:9px 12px;border-radius:8px;font-size:13px}
.success{background:#103c35;border:1px solid #1d7768;color:#83e4d2}
.error{background:#411b25;border:1px solid #8c3d4a;color:#ffc8d0}
.sheet{background:#162438;border:1px solid #2d425a;border-radius:12px;overflow:hidden;box-shadow:0 24px 70px rgba(0,0,0,.25)}
.sheet-head{display:flex;justify-content:space-between;align-items:center;padding:18px 20px;background:#18283e;border-bottom:1px solid #2d425a}
.eyebrow{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#43d1b6}
.sheet-head h1{margin:3px 0;font-size:26px;color:#fff}
.sheet-head p{margin:0;color:#7892aa;font-size:12px}
.status{text-transform:uppercase;font-size:11px;font-weight:900;padding:7px 10px;border-radius:99px;background:#26384d;color:#a4b8cb}
.status.sent{background:#123e36;color:#58dfc3}
.meta-grid{display:grid;grid-template-columns:repeat(4,1fr)}
.meta-grid label{padding:10px 12px;border-right:1px solid #2d4259;border-bottom:1px solid #2d4259}
.meta-grid span,.recipient span,.summary span,.data-grid span,footer span{display:block;color:#7892aa;font-size:10px;text-transform:uppercase;margin-bottom:5px}
.meta-grid input,.meta-grid select,.recipient input,textarea{width:100%;box-sizing:border-box;border:1px solid #334a62;background:#101e30;color:#e9f2f9;border-radius:7px;padding:9px;outline:none}
.recipient{display:flex;gap:15px;justify-content:space-between;align-items:end;padding:12px 14px;background:#11283a;border-bottom:1px solid #284055}
.recipient label{flex:1}
.recipient b{font-size:11px;color:#55d9bf;background:#143e38;border:1px solid #1b6c5d;padding:8px 10px;border-radius:7px}
.block{padding:16px 18px;border-bottom:1px solid #293d54}
.section-title{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px}
.section-title h2{font-size:15px;margin:0;color:#fff}
.section-title span{font-size:10px;color:#728ba3}
.summary{display:grid;grid-template-columns:repeat(5,1fr);gap:9px}
.summary article,.data-grid>div{background:#101e30;border:1px solid #2b4058;border-radius:8px;padding:10px}
.summary strong{display:block;font-size:20px;color:#fff}
.summary small{color:#60778e;font-size:9px}
.scroller{display:flex;gap:12px;overflow-x:auto;padding:4px 0 14px}
.mini{position:relative;flex:0 0 160px}
.mini-flag{position:absolute;top:6px;right:6px;font-size:10px;font-weight:800;color:#5eead4;background:#0F1B2D;border:1px solid #2D4259;border-radius:999px;padding:2px 6px}
.table-wrap{overflow:auto}
table{width:100%;min-width:900px;border-collapse:collapse}
th,td{padding:9px 10px;border:1px solid #30465e;text-align:center;font-size:11px;color:#dce9f5}
th{background:#22344a;color:#a7bbce;text-transform:uppercase;font-size:9px}
td{background:#101e30}
.empty{color:#738aa1;padding:20px}
.data-grid{display:grid;grid-template-columns:repeat(8,1fr);gap:8px}
.data-grid b{font-size:13px;color:#fff}
textarea{resize:vertical;min-height:110px}
footer{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;padding:14px 18px;background:#132136}
footer div{display:flex;justify-content:space-between;gap:8px;font-size:10px}
footer b{color:#e5eef6}
.recent{margin-top:18px}
.recent h2{color:#fff;font-size:16px;margin-bottom:10px}
.recent-list{display:grid;gap:8px}
.recent-item{display:grid;grid-template-columns:1fr auto;gap:10px;align-items:center;padding:10px 12px;border-radius:10px;background:#162438;border:1px solid #2d425a;color:#dce9f5}
.recent-open{display:grid;grid-template-columns:140px 1fr 80px;gap:10px;text-align:left;background:none;border:0;color:inherit;cursor:pointer}
.recent-delete{background:none;border:0;color:#fb7185;font-weight:800;cursor:pointer}
@media(max-width:1100px){.summary{grid-template-columns:repeat(3,1fr)}.data-grid{grid-template-columns:repeat(4,1fr)}}
@media(max-width:800px){.toolbar,.recipient{align-items:stretch;flex-direction:column}.meta-grid{grid-template-columns:repeat(2,1fr)}.summary{grid-template-columns:repeat(2,1fr)}.data-grid{grid-template-columns:repeat(2,1fr)}.recent-item,.recent-open{grid-template-columns:1fr}}
@media print{
  :global(body){background:#fff!important}
  .daily-report-page{padding:0;color:#000}
  .no-print{display:none!important}
  .sheet{max-width:none;border:0;border-radius:0;box-shadow:none}
  .sheet-head,.meta-grid label,.block,.summary article,.data-grid>div,th,td,footer{background:#fff!important;color:#000!important;border-color:#999!important}
  .sheet-head p,.section-title span,.summary span,.data-grid span,footer span{color:#444!important}
  textarea{background:#fff;color:#000;border-color:#999}
  @page{size:landscape;margin:8mm}
}
</style>
